import React, { ComponentProps, FC, useCallback } from 'react';
import { type FieldValues } from '@sravni/react-silent-authentication-form';
import cn from 'classnames';
import {
    CustomAuthInlineForm,
    AdvertisingBanner,
    type AuthInlineFormProps,
    type AgreementsOnClick,
} from '../../CustomAuthInlineForm';
import { AuthInlineFormConfig } from '../AuthInlineForm.config';
import { UserManagerFactory } from '../../UserManager';
import { useIsPage } from '../../../store/selectors/selection';
import { sessionStorage } from '../../../helpers/storage';
import { SESSION_STORAGE_WHITELABEL_TOKEN } from '../../../constants';
import { EventType, useAnalytics } from '../../hooks/analytics';
import styles from './AuthInlineForm.module.scss';

type Props = Pick<
    ComponentProps<typeof CustomAuthInlineForm>,
    'onLogin' | 'onLoginFailed' | 'defaultValues' | 'additionalValidationSchema' | 'getOTPButtonProps'
> & {
    className?: string;
};

export const AuthInlineForm: FC<Props> = (props) => {
    const {
        onLogin: onLoginOutside,
        onLoginFailed,
        defaultValues,
        className,
        additionalValidationSchema,
        children,
        getOTPButtonProps,
    } = props;
    const { pushEvent } = useAnalytics();
    const { isWlPage } = useIsPage();

    const { metaInfo, texts: AuthInlineTexts } = AuthInlineFormConfig;

    const onLogin = useCallback(
        async (data: FieldValues) => {
            if (isWlPage) {
                const accessToken = await new UserManagerFactory().getAccessToken();
                if (accessToken) {
                    sessionStorage.setItem(SESSION_STORAGE_WHITELABEL_TOKEN, accessToken);
                }
            }
            onLoginOutside?.(data);
        },
        [onLoginOutside, isWlPage],
    );

    const onConsentsScreenOpen: ComponentProps<typeof CustomAuthInlineForm>['onConsentsScreenOpen'] =
        useCallback(() => {
            pushEvent({
                event: EventType.mainEvent,
                eventAction: 'Показ',
                eventLabel: 'Согласие на рекламу и обработку',
            });
        }, [pushEvent]);

    const onConsentsSubmitClick = useCallback(
        (data: AgreementsOnClick) => {
            const { agreementAdvertising, agreementPersonalData, isSubmitted } = data;

            let eventLabel = '';

            if (isSubmitted) {
                if (agreementPersonalData) {
                    eventLabel = agreementAdvertising
                        ? 'Согласие на рекламу и обработку'
                        : 'Согласие на рекламу и обработку | Без галочки';
                }
            } else {
                eventLabel = 'Согласие на рекламу и обработку | Без галочки после клика';
            }

            if (eventLabel) {
                pushEvent({
                    event: EventType.mainEvent,
                    eventAction: 'Клик',
                    eventLabel,
                });
            }
        },
        [pushEvent],
    );

    const onChangeAgreementPersonalData: NonNullable<AuthInlineFormProps['onChangeAgreementPersonalData']> =
        useCallback(
            (event) => {
                const { checked } = event?.target || {};
                if (!checked) {
                    pushEvent({
                        event: EventType.mainEvent,
                        eventAction: 'Клик',
                        eventLabel: 'Согласие на рекламу и обработку | Без галочки | Снял согласие обработки',
                    });
                }
            },
            [pushEvent],
        );

    const onChangeAgreementAdvertising: NonNullable<AuthInlineFormProps['onChangeAgreementAdvertising']> = useCallback(
        (event) => {
            const { checked } = event?.target || {};
            if (checked) {
                pushEvent({
                    event: EventType.mainEvent,
                    eventAction: 'Клик',
                    eventLabel: 'Согласие на рекламу и обработку | Поставил галочку',
                });
            }
        },
        [pushEvent],
    );

    const onChangePhoneClick = useCallback(() => {
        pushEvent({
            event: EventType.mainEvent,
            eventCategory: 'Подбор кредита',
            eventAction: 'Клик',
            eventLabel: 'Изменить номер',
        });
    }, [pushEvent]);

    const onInvalidSmsCodeError = useCallback(() => {
        pushEvent({
            event: EventType.mainEvent,
            eventCategory: 'Подбор кредита',
            eventAction: 'Клик',
            eventLabel: 'Неверный код СМС',
        });
    }, [pushEvent]);

    const onClose = useCallback(() => {
        pushEvent({
            event: EventType.mainEvent,
            eventCategory: 'Подбор кредита',
            eventAction: 'Клик',
            eventLabel: 'Закрыть pop-up код из СМС',
        });
    }, [pushEvent]);

    const onGetNewOTPClick = useCallback(() => {
        pushEvent({
            event: EventType.mainEvent,
            eventCategory: 'Подбор кредита',
            eventAction: 'Клик',
            eventLabel: 'Получить новый код СМС',
        });
    }, [pushEvent]);

    return (
        <CustomAuthInlineForm
            getOTPButtonProps={getOTPButtonProps}
            additionalValidationSchema={additionalValidationSchema}
            className={cn(styles.silentAuthInlineForm, className)}
            defaultValues={defaultValues}
            onLogin={onLogin}
            onLoginFailed={onLoginFailed}
            consentsScreenTitle={AuthInlineTexts.consentsScreenTitle}
            advertisingBanner={
                <AdvertisingBanner title={AuthInlineTexts.bannerTitle} list={AuthInlineTexts.bannerAdsTextsList} />
            }
            advertisingCheckboxLabel={AuthInlineTexts.advertisingCheckboxLabel}
            userManager={new UserManagerFactory().createUserManager()}
            metaInfo={metaInfo}
            onConsentsScreenOpen={onConsentsScreenOpen}
            onConsentsSubmitClick={onConsentsSubmitClick}
            onChangeAgreementPersonalData={onChangeAgreementPersonalData}
            onChangeAgreementAdvertising={onChangeAgreementAdvertising}
            onChangePhoneClick={onChangePhoneClick}
            onInvalidSmsCodeError={onInvalidSmsCodeError}
            onClose={onClose}
            onGetNewOTPClick={onGetNewOTPClick}>
            <CustomAuthInlineForm.Before>
                <div className="h-pb-12">{children}</div>
            </CustomAuthInlineForm.Before>
        </CustomAuthInlineForm>
    );
};
